import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Loading stand-in for a report result: summary cards (when the report has them)
 * and a table shaped like the rows that will replace it.
 */
export const ReportRunSkeleton = ({
  summaryCards = 0,
  columnCount = 3,
  rowCount = 8,
}: {
  summaryCards?: number;
  columnCount?: number;
  rowCount?: number;
}) => {
  const { t } = useTranslation();
  const columns = Math.max(1, columnCount);

  return (
    <div className="space-y-4" aria-busy="true">
      <span className="sr-only">{t('state.loading')}</span>
      {summaryCards > 0 ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {Array.from({ length: summaryCards }, (_, index) => (
            <Card key={index}>
              <CardContent className="space-y-2 py-4">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-36" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full table-fixed text-body-sm">
            <thead>
              <tr className="border-b border-border">
                {Array.from({ length: columns }, (_, index) => (
                  <th key={index} className="px-4 py-3">
                    <Skeleton className="h-3 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }, (_, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border last:border-0">
                  {Array.from({ length: columns }, (_, colIndex) => (
                    <td key={colIndex} className="px-4 py-3">
                      <Skeleton
                        className={
                          colIndex === columns - 1 ? 'ml-auto h-4 w-24' : 'h-4 w-full max-w-[12rem]'
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export const reportRunSummaryCardCount = (
  reportKey: string | undefined,
  totals:
    | {
        totalAssetsMinor?: number;
        revenueMinor?: number;
        amountMinor?: number;
        byCurrency?: unknown;
      }
    | undefined,
): number => {
  if (reportKey === 'balance_sheet' || reportKey === 'statement_of_activities') return 3;
  if (totals?.totalAssetsMinor !== undefined || totals?.revenueMinor !== undefined) return 3;
  if (Array.isArray(totals?.byCurrency)) return Math.min(totals.byCurrency.length, 3);
  if (totals?.amountMinor !== undefined) return 1;
  return 0;
};
